// ═════════════════════════════════════════════════════════════════════════════
// Auto tab — NHCX coverage eligibility
//
// Three phases in one card: form → waiting (polling) → result.
// Contract + field rules: COVERAGE_ELIGIBILITY_UI_INTEGRATION.md (repo root).
//
// The small presentational helpers below (Field / DisplayField / styles) are
// deliberately local copies of the ones in IPDBillingInsurance.js: that page
// imports this component, so importing them back out would be a circular import.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { MenuItem } from "@mui/material";
import { C } from "../constants";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdDateField from "../../ipd/utils/IpdDateField";
import IpdButton from "../../ipd/utils/IpdButton";
import { IPD_FIELD_COLORS } from "../../ipd/utils/ipdFieldStyles";
import SvgIcon from "../SvgIcon";
import SnomedSearchField from "./SnomedSearchField";
import Icd10MappedField from "./Icd10MappedField";
import {
  submitEligibilityCheck,
  pollEligibilityResponse,
  normalizeCheckError,
} from "../coverage-eligibility-api";

// ── API vocabulary ───────────────────────────────────────────────────────────

// Send the enum VALUE — "auth-requirements" with a hyphen; auth_requirements is rejected.
// The hint is the question each purpose actually asks the payer, so the clinical-items
// section appearing and disappearing is explained before it happens.
const PURPOSES = [
  { value: "validation", label: "Validation", needsItems: false, hint: "Is this policy active right now?" },
  { value: "benefits", label: "Benefits", needsItems: true, hint: "What does this policy cover? Needs clinical items." },
  // { value: "discovery", label: "Discovery", needsItems: false, hint: "What policies does this patient have?" },
  // { value: "auth-requirements", label: "Auth Requirements", needsItems: true, hint: "What pre-authorisation is needed? Needs clinical items." },
];

// Code and display must agree. The API does not check the pairing and will happily
// build a misleading FHIR bundle from a mismatch, so they are picked as one value.
const PHYSICAL_TYPES = [
  { code: "bu", display: "Building" },
  { code: "wi", display: "Wing" },
  { code: "wa", display: "Ward" },
  { code: "fl", display: "Floor" },
  { code: "ro", display: "Room" },
  { code: "bd", display: "Bed" },
];

const RELATIONSHIPS = ["self", "spouse", "child", "parent", "common", "other", "injured", "polholder"];

// Case-insensitive server-side, but a free-text box here just buys a 422 round trip.
const GENDERS = ["male", "female", "other", "unknown"];

// The five NHCX insurer fields have no source in the EMR — the payer master carries
// only code/name/type/active. Until they live in a backend config, they come from
// here; "custom" reveals them as inputs for an insurer not yet listed.
const INSURER_REGISTRY = [
  {
    key: "dummy-sandbox",
    label: "Dummy Payer (NHCX sandbox)",
    payer_code: "1000003538@hcx",
    insurer_rohini_id: "1234567",
    insurer_name: "Dummy Payer",
    insurer_phone: "+914442201234",
    insurer_email: "support@dummypayer.in",
    insurer_policy_namespace_uri: "https://dummypayer.in/policy/",
  },
];

const EMPTY_INSURER = {
  payer_code: "",
  insurer_rohini_id: "",
  insurer_name: "",
  insurer_phone: "",
  insurer_email: "",
  insurer_policy_namespace_uri: "",
};

// `type` picks the widget: gender is an enum and dob a date, so neither is a text box.
// Everything else is free text with the format hint the API expects.
const OVERRIDE_FIELDS = {
  override_patient_abha_id: { label: "Patient ABHA ID", hint: "username@abdm / username@sbx, or 14 digits" },
  override_patient_aadhaar: { label: "Patient Aadhaar", hint: "12 digits" },
  override_patient_phone: { label: "Patient phone", hint: "+91 is added automatically" },
  override_patient_gender: { label: "Patient gender", type: "gender" },
  override_patient_dob: { label: "Patient date of birth", type: "date" },
  override_facility_name: { label: "Facility name" },
  override_facility_abdm_id: { label: "Facility ABDM ID", hint: "ABDM facility ID, e.g. IN3210000017" },
  override_facility_phone: { label: "Facility phone", hint: "+91 is added automatically" },
  override_facility_email: { label: "Facility email" },
  override_doctor_registration_no: { label: "Doctor registration no.", hint: "Medical council registration" },
  override_insurance_policy_no: { label: "Insurance policy no." },
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20; // ~1 minute before offering a manual retry

// Shown when Skip is used so the waiting / result screens can be walked without NHCX.
const SAMPLE_ELIGIBILITY_RESULT = {
  use_case: "coverage_eligibility",
  status: "complete",
  eligibility: {
    outcome: "complete",
    disposition: "Policy is active",
    inforce: true,
    benefit_period: { start: "2026-01-01", end: "2026-12-31" },
    insurance_items: [
      {
        name: "In-patient hospitalisation",
        description: "Cashless in-patient treatment at network hospitals.",
        excluded: false,
        authorization_required: true,
        benefit: [{ type: "benefit", allowedMoney: { value: 500000, currency: "INR" } }],
      },
    ],
    errors: [],
  },
  received_at: "2026-01-01T00:00:00Z",
};

const EMPTY_ITEM = {
  product_or_service_code: "",
  product_or_service_display: "",
  hasDiagnosis: false,
  diagnosis_code: "",
  diagnosis_display: "",
};

// ── local styles (mirrors the Manual tab) ────────────────────────────────────

const cardStyle = {
  background: C.card,
  borderRadius: "12px",
  border: `1px solid ${C.border}`,
  boxShadow: "0 2px 8px rgba(13,35,63,0.07)",
};
const labelStyle = {
  fontSize: "11px",
  fontWeight: "700",
  color: C.textSub,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  display: "block",
  lineHeight: 1.4,
  marginBottom: "6px",
};
const sectionLabelStyle = {
  fontSize: "12px",
  fontWeight: "800",
  color: C.text,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "6px",
};
const sectionHintStyle = {
  fontSize: "12px",
  color: C.textSub,
  marginBottom: "14px",
  lineHeight: 1.45,
};
const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  columnGap: "24px",
  rowGap: "8px",
  alignItems: "start",
};
const sectionBoxStyle = {
  background: C.bg,
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  padding: "16px 18px 18px",
};
const routingBoxStyle = {
  marginTop: "16px",
  paddingTop: "14px",
  borderTop: `1px solid ${C.border}`,
};
const itemRowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 0.9fr) minmax(0, 1.4fr)",
  columnGap: "20px",
  rowGap: "8px",
  alignItems: "start",
};
const innerCardStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  padding: "16px",
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};

function DisplayField({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      <IpdTextField
        fullWidth
        disabled
        value={value || "—"}
        title={value || ""}
        sx={{
          "& .MuiOutlinedInput-root.Mui-disabled": {
            boxShadow: IPD_FIELD_COLORS.shadow,
            "& fieldset": { borderColor: IPD_FIELD_COLORS.border },
          },
          "& .MuiOutlinedInput-input.Mui-disabled": {
            WebkitTextFillColor: value ? C.text : C.textSub,
            fontWeight: 600,
          },
        }}
      />
    </div>
  );
}

function Field({ label, required, hint, hintTone, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>
        {label}
        {required ? " *" : ""}
      </label>
      {children}
      <div
        title={hint || undefined}
        style={{
          fontSize: "11px",
          color: hintTone === "warning" ? C.warning : C.textSub,
          marginTop: "6px",
          minHeight: "16px",
          lineHeight: 1.3,
          fontWeight: hintTone === "warning" ? 600 : 400,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {hint || "\u00a0"}
      </div>
    </div>
  );
}

function Pill({ tone = "grey", children }) {
  const tones = {
    grey: { bg: "#f1f3f5", fg: C.textSub, br: C.border },
    green: { bg: "#e9f7ef", fg: C.success, br: "#b7e4c7" },
    red: { bg: "#fdecec", fg: C.danger, br: "#f5c2c0" },
    blue: { bg: C.accent, fg: C.primary, br: "#bfe3fb" },
  };
  const t = tones[tone] || tones.grey;
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: "700",
        padding: "3px 9px",
        borderRadius: "20px",
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.br}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function PreviewBanner() {
  return (
    <div
      style={{
        fontSize: "12px",
        fontWeight: "600",
        color: "#B45309",
        background: "#FFF8E1",
        border: "1px solid #FFE082",
        borderRadius: "8px",
        padding: "8px 12px",
        textAlign: "left",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      Preview — sample data so you can see the next screens. This is not a payer decision.
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

// Local (not UTC) calendar date — toISOString() would roll over a day in IST evenings.
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const isPastDate = (iso) => Boolean(iso) && iso < todayISO();

// ── component ────────────────────────────────────────────────────────────────

export default function CoverageEligibilityPanel({
  admissionId,
  patient,
  display,
  memberCardNumber,
  insurancePlan,
  onCopyToManual,
  onContinueToPreauth,
}) {
  const planInsurerKey = INSURER_REGISTRY.some((i) => i.key === insurancePlan?.insurerKey)
    ? insurancePlan.insurerKey
    : (INSURER_REGISTRY[0]?.key || "custom");
  const [phase, setPhase] = useState("form"); // form | waiting | result
  const [request, setRequest] = useState({
    purpose: "benefits",
    insurerKey: planInsurerKey,
    coverage_subscriber_id: memberCardNumber || "",
    coverage_relationship_code: "self",
    coverage_period_end: "",
  });
  const [customInsurer, setCustomInsurer] = useState(EMPTY_INSURER);
  const [location, setLocation] = useState({
    // No DB source for location — always from the UI. Seeded from the admission's
    // ward so the common case is one field (phone) away from complete.
    location_identifier: "",
    location_name: patient?.ward || "",
    location_phone: "",
    location_physical_type_code: "wa",
    location_physical_type_display: "Ward",
    // Optional. location_alias is a string[] on the wire; held here as the
    // comma-separated string the user actually types and split at build time.
    location_alias: "",
    location_description: "",
    location_fax: "",
    location_url: "",
  });
  const [locationOpen, setLocationOpen] = useState(true);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);

  // Overrides resolve as "your value first, DB as fallback", so a seeded value silently
  // wins over the hospital record — these two are pinned to the NHCX sandbox pair the
  // payer recognises, which is why they ignore both the admission's policy number and
  // the facility's real ABDM id. Revert to `display?.policy || ""` / omit the facility
  // id before this reaches a live payer.
  const [overrides, setOverrides] = useState({
    override_facility_abdm_id: "IN3210000017",
    override_insurance_policy_no: insurancePlan?.policyNo || "100217",
  });
  const [revealed, setRevealed] = useState([]); // override names the server asked for

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null); // normalized {message, fieldErrors, missing}
  const [correlationId, setCorrelationId] = useState("");
  const [apiCallId, setApiCallId] = useState(""); // support/debugging reference
  const [attempts, setAttempts] = useState(0);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [pollCycle, setPollCycle] = useState(0); // bumped to restart polling after a timeout
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(false);

  const selectedPurpose = PURPOSES.find((p) => p.value === request.purpose);
  const needsItems = Boolean(selectedPurpose?.needsItems);
  const insurer =
    request.insurerKey === "custom"
      ? customInsurer
      : INSURER_REGISTRY.find((i) => i.key === request.insurerKey) || EMPTY_INSURER;

  const setReq = (key, value) => setRequest((p) => ({ ...p, [key]: value }));
  const setLoc = (key, value) => setLocation((p) => ({ ...p, [key]: value }));

  // The insurance details load asynchronously, so the member card number usually
  // arrives after this panel has mounted and missed it in the initial state. Fill it
  // in when it lands, but never over something the user has already typed.
  useEffect(() => {
    if (!memberCardNumber) return;
    setRequest((p) => (p.coverage_subscriber_id ? p : { ...p, coverage_subscriber_id: memberCardNumber }));
  }, [memberCardNumber]);

  // ── polling ────────────────────────────────────────────────────────────────
  // 404 means "no answer yet" and so does status "partial" — both keep the spinner up.
  useEffect(() => {
    if (phase !== "waiting" || !correlationId || preview) return undefined;
    let cancelled = false;
    let timer;

    const tick = async (n) => {
      try {
        const res = await pollEligibilityResponse(correlationId);
        if (cancelled) return;
        if (res?.pending || res?.status === "partial") {
          if (n >= MAX_POLLS) {
            setPollExhausted(true);
            return;
          }
          setAttempts(n);
          timer = setTimeout(() => tick(n + 1), POLL_INTERVAL_MS);
          return;
        }
        setResult(res);
        setPhase("result");
      } catch (err) {
        if (cancelled) return;
        setError(normalizeCheckError(err));
        setPollExhausted(true);
      }
    };

    timer = setTimeout(() => tick(1), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phase, correlationId, pollCycle, preview]);

  const validate = () => {
    if (!admissionId || !(Number(admissionId) > 0)) return "This admission has no numeric IPD id.";
    if (!insurer.payer_code) return "Select a payer.";
    if (!insurer.insurer_rohini_id) return "Insurer ROHINI ID is required.";
    if (!insurer.insurer_phone) return "Insurer phone is required.";
    if (!insurer.insurer_email) return "Insurer email is required.";
    if (!insurer.insurer_policy_namespace_uri) return "Insurer policy namespace URI is required.";
    if (!request.coverage_subscriber_id.trim()) return "Member / Subscriber ID is required.";
    if (!request.coverage_period_end) return "Policy valid-until date is required.";
    if (!location.location_identifier.trim()) return "Location identifier is required.";
    if (!location.location_name.trim()) return "Location name is required.";
    if (!location.location_phone.trim()) return "Location phone is required.";
    if (needsItems) {
      const filled = items.filter((i) => i.product_or_service_code.trim());
      // Typing a service name without picking one of the search results leaves no
      // code, and the code is what NHCX is actually sent — say that, rather than
      // reporting the item as missing entirely.
      const unresolved = items.some(
        (i) => i.product_or_service_display.trim() && !i.product_or_service_code.trim()
      );
      if (unresolved) return "Pick a service from the search results — the SNOMED code comes from the selection.";
      if (!filled.length) return `At least one clinical item is required for "${request.purpose}".`;
      // Enforced server-side too, but a round trip for this is a poor experience.
      const halfDiagnosis = filled.some(
        (i) => i.hasDiagnosis && (!i.diagnosis_code.trim() || !i.diagnosis_display.trim())
      );
      if (halfDiagnosis) return "Diagnosis code and description must both be filled, or both cleared.";
    }
    return "";
  };

  const buildPayload = () => {
    const payload = {
      ipd_id: Number(admissionId),
      purpose: request.purpose,
      payer_code: insurer.payer_code,
      insurer_rohini_id: insurer.insurer_rohini_id,
      insurer_phone: insurer.insurer_phone,
      insurer_email: insurer.insurer_email,
      insurer_policy_namespace_uri: insurer.insurer_policy_namespace_uri,
      coverage_subscriber_id: request.coverage_subscriber_id.trim(),
      coverage_relationship_code: request.coverage_relationship_code,
      coverage_period_end: request.coverage_period_end,
      location_identifier: location.location_identifier.trim(),
      location_name: location.location_name.trim(),
      location_phone: location.location_phone.trim(),
      location_physical_type_code: location.location_physical_type_code,
      location_physical_type_display: location.location_physical_type_display,
    };
    if (insurer.insurer_name) payload.insurer_name = insurer.insurer_name;

    // Optional location detail — omitted entirely rather than sent blank, so the
    // server's own defaults ([] / null) apply when the user has filled nothing in.
    const alias = location.location_alias
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (alias.length) payload.location_alias = alias;
    ["location_description", "location_fax", "location_url"].forEach((k) => {
      if (location[k].trim()) payload[k] = location[k].trim();
    });

    // items is ignored for validation/discovery — omit it rather than send noise.
    if (needsItems) {
      payload.items = items
        .filter((i) => i.product_or_service_code.trim())
        .map((i) => {
          const entry = {
            product_or_service_code: i.product_or_service_code.trim(),
            product_or_service_display: i.product_or_service_display.trim(),
          };
          // All-or-nothing: one without the other is rejected.
          if (i.hasDiagnosis && i.diagnosis_code.trim() && i.diagnosis_display.trim()) {
            entry.diagnosis_code = i.diagnosis_code.trim();
            entry.diagnosis_display = i.diagnosis_display.trim();
          }
          return entry;
        });
    }

    Object.entries(overrides).forEach(([k, v]) => {
      if (String(v || "").trim()) payload[k] = String(v).trim();
    });
    return payload;
  };

  const handleSubmit = async () => {
    const problem = validate();
    if (problem) {
      setError({ message: problem, fieldErrors: [], missing: [] });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitEligibilityCheck(buildPayload());
      const id = res?.correlation_id;
      if (!id) {
        setError({ message: "The service accepted the request but returned no correlation id.", fieldErrors: [], missing: [] });
        return;
      }
      setCorrelationId(id);
      setApiCallId(res?.api_call_id || "");
      setAttempts(0);
      setPollExhausted(false);
      setResult(null);
      setPreview(false);
      setPhase("waiting");
    } catch (err) {
      const normalized = normalizeCheckError(err);
      setError(normalized);
      // Strategy A: the server has told us exactly which overrides it needs — reveal
      // those inputs (pre-seeded where we can guess) instead of asking for all eleven.
      const asked = [...new Set(normalized.missing.flatMap((m) => m.overrides))];
      if (asked.length) {
        setRevealed((prev) => [...new Set([...prev, ...asked])]);
        setOverrides((prev) => {
          const next = { ...prev };
          asked.forEach((name) => {
            if (next[name] === undefined) {
              next[name] =
                name === "override_patient_gender" ? String(patient?.gender || "").toLowerCase() : "";
            }
          });
          return next;
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = useCallback(() => {
    setPhase("form");
    setResult(null);
    setCorrelationId("");
    setApiCallId("");
    setAttempts(0);
    setPollExhausted(false);
    setError(null);
    setPreview(false);
  }, []);

  const handleSkip = () => {
    setError(null);
    setCorrelationId("preview");
    setApiCallId("");
    setAttempts(0);
    setPollExhausted(false);
    setResult(null);
    setPreview(true);
    setPhase("waiting");
  };

  const handleSkipToResult = () => {
    setPreview(true);
    setCorrelationId((id) => id || "preview");
    setResult(SAMPLE_ELIGIBILITY_RESULT);
    setPhase("result");
  };

  // ── header ─────────────────────────────────────────────────────────────────

  const statusPill =
    phase === "waiting" ? (
      <Pill tone="blue">Waiting for payer</Pill>
    ) : phase === "result" ? (
      <Pill tone={result?.eligibility?.inforce ? "green" : "red"}>
        {result?.eligibility?.inforce ? "Policy active" : "Not in force"}
      </Pill>
    ) : (
      <Pill>Not checked</Pill>
    );

  return (
    <div style={cardStyle}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SvgIcon name="globe" size={16} color={C.primary} />
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: C.text }}>
            NHCX Coverage Eligibility · Automated Check
          </h3>
        </div>
        {statusPill}
      </div>

      <div style={{ padding: "20px 20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
        {phase === "waiting" ? (
          <WaitingState
            correlationId={correlationId}
            apiCallId={apiCallId}
            attempts={attempts}
            exhausted={pollExhausted}
            error={error}
            preview={preview}
            onKeepWaiting={() => {
              setPollExhausted(false);
              setError(null);
              setAttempts(0);
              setPollCycle((c) => c + 1); // restarts the polling effect, same correlation id
            }}
            onSkipToResult={handleSkipToResult}
            onStop={handleReset}
          />
        ) : phase === "result" ? (
          <ResultState
            result={result}
            correlationId={correlationId}
            apiCallId={apiCallId}
            preview={preview}
            onRunAgain={handleReset}
            onCopyToManual={onCopyToManual ? () => onCopyToManual(correlationId) : null}
            onContinueToPreauth={onContinueToPreauth}
          />
        ) : (
          <>
            {/* ── 1. PAYER / POLICY (read-only, from registration) ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionLabelStyle}>
                <SvgIcon name="building" size={14} color={C.textSub} /> Payer / Policy Information
              </div>
              <div style={gridStyle}>
                <DisplayField label="Payer Type" value={display?.payerType} />
                <DisplayField label="Insurance / Payer Name" value={display?.provider} />
                <DisplayField label="Policy No." value={display?.policy} />
                <DisplayField label="Patient Name" value={patient?.name} />
                <DisplayField label="Admission (IPD) ID" value={admissionId} />
              </div>
            </div>

            {/* ── 2. CHECK REQUEST ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionLabelStyle}>
                <SvgIcon name="send" size={14} color={C.textSub} /> Check Request
              </div>
              {selectedPurpose?.hint && <div style={sectionHintStyle}>{selectedPurpose.hint}</div>}
              <div style={gridStyle}>
                <Field label="Purpose" required>
                  <IpdTextField select fullWidth value={request.purpose} onChange={(e) => setReq("purpose", e.target.value)}>
                    {PURPOSES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                    ))}
                  </IpdTextField>
                </Field>
                <Field label="Payer (NHCX registry)" required>
                  <IpdTextField select fullWidth value={request.insurerKey} onChange={(e) => setReq("insurerKey", e.target.value)}>
                    {INSURER_REGISTRY.map((i) => (
                      <MenuItem key={i.key} value={i.key}>{i.label}</MenuItem>
                    ))}
                    <MenuItem value="custom">Other — enter manually</MenuItem>
                  </IpdTextField>
                </Field>
                <Field label="Member / Subscriber ID" required>
                  <IpdTextField
                    fullWidth
                    value={request.coverage_subscriber_id}
                    onChange={(e) => setReq("coverage_subscriber_id", e.target.value)}
                    placeholder="Member ID on the card"
                    title="The member ID on the card — not the policy number."
                  />
                </Field>
                <Field
                  label="Policy Valid Until"
                  required
                  hint={isPastDate(request.coverage_period_end) ? "This date is in the past — the policy has expired." : ""}
                  hintTone={isPastDate(request.coverage_period_end) ? "warning" : undefined}
                >
                  <IpdDateField fullWidth value={request.coverage_period_end} onChange={(iso) => setReq("coverage_period_end", iso)} />
                </Field>
                <Field label="Relationship to Subscriber">
                  <IpdTextField
                    select
                    fullWidth
                    value={request.coverage_relationship_code}
                    onChange={(e) => setReq("coverage_relationship_code", e.target.value)}
                  >
                    {RELATIONSHIPS.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </IpdTextField>
                </Field>
              </div>

              {request.insurerKey === "custom" ? (
                <div style={routingBoxStyle}>
                  <div style={{ ...sectionLabelStyle, marginBottom: "12px" }}>Payer routing</div>
                  <div style={gridStyle}>
                    <Field label="Payer Code" required hint="NHCX registry code, e.g. 1000003538@hcx">
                      <IpdTextField fullWidth value={customInsurer.payer_code} onChange={(e) => setCustomInsurer((p) => ({ ...p, payer_code: e.target.value }))} placeholder="1000003538@hcx" />
                    </Field>
                    <Field label="Insurer ROHINI ID" required hint="13 digits in production">
                      <IpdTextField fullWidth value={customInsurer.insurer_rohini_id} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_rohini_id: e.target.value }))} />
                    </Field>
                    <Field label="Insurer Name">
                      <IpdTextField fullWidth value={customInsurer.insurer_name} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_name: e.target.value }))} />
                    </Field>
                    <Field label="Insurer Phone" required hint="+91 is added automatically">
                      <IpdTextField fullWidth value={customInsurer.insurer_phone} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_phone: e.target.value }))} placeholder="9876543210" />
                    </Field>
                    <Field label="Insurer Email" required>
                      <IpdTextField fullWidth value={customInsurer.insurer_email} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_email: e.target.value }))} />
                    </Field>
                    <Field label="Policy Namespace URI" required hint="From the insurer's onboarding docs">
                      <IpdTextField fullWidth value={customInsurer.insurer_policy_namespace_uri} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_policy_namespace_uri: e.target.value }))} placeholder="https://insurer.in/policy/" />
                    </Field>
                  </div>
                </div>
              ) : (
                <div style={routingBoxStyle}>
                  <div style={{ ...sectionLabelStyle, marginBottom: "12px" }}>Payer routing</div>
                  <div style={gridStyle}>
                    <DisplayField label="Payer Code" value={insurer.payer_code} />
                    <DisplayField label="ROHINI ID" value={insurer.insurer_rohini_id} />
                    <DisplayField label="Insurer Phone" value={insurer.insurer_phone} />
                    <DisplayField label="Insurer Email" value={insurer.insurer_email} />
                    <DisplayField label="Policy Namespace" value={insurer.insurer_policy_namespace_uri} />
                  </div>
                </div>
              )}
            </div>

            {/* ── 3. HOSPITAL LOCATION ── */}
            <div style={sectionBoxStyle}>
              <div style={{ ...sectionLabelStyle, marginBottom: locationOpen ? "14px" : 0, justifyContent: "space-between" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <SvgIcon name="bed" size={14} color={C.textSub} /> Hospital Location
                  {!locationOpen && (
                    <span style={{ fontSize: "11px", fontWeight: "600", color: C.textSub, textTransform: "none", letterSpacing: 0 }}>
                      {location.location_name || "not set"} · {location.location_physical_type_display}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => setLocationOpen((v) => !v)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: C.primary, fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "5px" }}
                >
                  {locationOpen ? "Collapse" : "Expand"}
                  <SvgIcon name={locationOpen ? "chevron-up" : "chevron-down"} size={13} color={C.primary} />
                </button>
              </div>
              {locationOpen && (
                <div style={gridStyle}>
                  <Field label="Identifier" required hint="Wing / floor / unit code">
                    <IpdTextField fullWidth value={location.location_identifier} onChange={(e) => setLoc("location_identifier", e.target.value)} placeholder="B1-SF2" />
                  </Field>
                  <Field label="Name" required>
                    <IpdTextField fullWidth value={location.location_name} onChange={(e) => setLoc("location_name", e.target.value)} placeholder="South Wing, Second Floor" />
                  </Field>
                  <Field label="Phone" required hint="+91 is added automatically">
                    <IpdTextField fullWidth value={location.location_phone} onChange={(e) => setLoc("location_phone", e.target.value)} placeholder="3322001234" />
                  </Field>
                  <Field label="Physical Type" required>
                    <IpdTextField
                      select
                      fullWidth
                      value={location.location_physical_type_code}
                      onChange={(e) => {
                        const picked = PHYSICAL_TYPES.find((t) => t.code === e.target.value);
                        setLocation((p) => ({
                          ...p,
                          location_physical_type_code: picked.code,
                          location_physical_type_display: picked.display,
                        }));
                      }}
                    >
                      {PHYSICAL_TYPES.map((t) => (
                        <MenuItem key={t.code} value={t.code}>{t.display}</MenuItem>
                      ))}
                    </IpdTextField>
                  </Field>
                  <Field label="Aliases" hint="Other names for this location, comma separated">
                    <IpdTextField fullWidth value={location.location_alias} onChange={(e) => setLoc("location_alias", e.target.value)} placeholder="SW2, Surgical Ward 2" />
                  </Field>
                  <Field label="Description">
                    <IpdTextField fullWidth value={location.location_description} onChange={(e) => setLoc("location_description", e.target.value)} placeholder="Second floor, general surgery ward" />
                  </Field>
                  <Field label="Fax" hint="+91 is added automatically">
                    <IpdTextField fullWidth value={location.location_fax} onChange={(e) => setLoc("location_fax", e.target.value)} placeholder="3322001235" />
                  </Field>
                  <Field label="URL">
                    <IpdTextField fullWidth value={location.location_url} onChange={(e) => setLoc("location_url", e.target.value)} placeholder="https://hospital.in/southwing" />
                  </Field>
                </div>
              )}
            </div>

            {/* ── 4. CLINICAL ITEMS (benefits / auth-requirements only) ── */}
            {needsItems && (
              <div style={sectionBoxStyle}>
                <div style={sectionLabelStyle}>
                  <SvgIcon name="pill" size={14} color={C.textSub} /> Clinical Items
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {items.map((item, idx) => (
                    <div key={idx} style={innerCardStyle}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: "12px", fontWeight: "700", color: C.text }}>Item {idx + 1}</span>
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                            style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", color: C.danger, fontSize: "12px", fontWeight: "600" }}
                          >
                            <SvgIcon name="trash" size={13} color={C.danger} /> Remove
                          </button>
                        )}
                      </div>
                      <div style={itemRowStyle}>
                        <Field label="Service" required hint="Search by name — the SNOMED code comes from the concept you pick">
                          <SnomedSearchField
                            term={item.product_or_service_display}
                            code={item.product_or_service_code}
                            semanticTag="procedure"
                            placeholder="e.g. stent placement"
                            // Editing the text invalidates the code it came from: the two
                            // are sent as a pair, so they are cleared and set together.
                            onTermChange={(value) =>
                              setItems((prev) =>
                                prev.map((it, i) =>
                                  i === idx
                                    ? {
                                        ...it,
                                        product_or_service_display: value,
                                        product_or_service_code: "",
                                        hasDiagnosis: false,
                                        diagnosis_code: "",
                                        diagnosis_display: "",
                                      }
                                    : it
                                )
                              )
                            }
                            onSelect={(concept) =>
                              setItems((prev) =>
                                prev.map((it, i) =>
                                  i === idx
                                    ? {
                                        ...it,
                                        product_or_service_display: concept.term,
                                        product_or_service_code: concept.code,
                                        hasDiagnosis: false,
                                        diagnosis_code: "",
                                        diagnosis_display: "",
                                      }
                                    : it
                                )
                              )
                            }
                          />
                        </Field>
                        <DisplayField label="Service Code (SNOMED CT)" value={item.product_or_service_code} />
                        <Field
                          label="Diagnosis (ICD-10)"
                          hint={
                            item.product_or_service_code
                              ? "Filled from snomed/map_icd when you pick a service."
                              : "Pick a service first — ICD-10 is mapped from its SNOMED code."
                          }
                        >
                          {item.product_or_service_code ? (
                            <Icd10MappedField
                              snomedCode={item.product_or_service_code}
                              code={item.diagnosis_code}
                              display={item.diagnosis_display}
                              compact
                              onChange={(row) =>
                                setItems((prev) =>
                                  prev.map((it, i) =>
                                    i === idx
                                      ? {
                                          ...it,
                                          hasDiagnosis: Boolean(row.code),
                                          diagnosis_code: row.code || "",
                                          diagnosis_display: row.display || "",
                                        }
                                      : it
                                  )
                                )
                              }
                            />
                          ) : (
                            <IpdTextField fullWidth disabled value="" placeholder="Select a service first" />
                          )}
                        </Field>
                      </div>
                    </div>
                  ))}
                  <div>
                    <IpdButton variant="outlined" onClick={() => setItems((prev) => [...prev, { ...EMPTY_ITEM }])} startIcon={<SvgIcon name="plus" size={14} color={C.primary} />}>
                      Add item
                    </IpdButton>
                  </div>
                </div>
              </div>
            )}

            {/* ── 5. OVERRIDES — revealed only when the server says they're missing ── */}
            {revealed.length > 0 && (
              <div style={sectionBoxStyle}>
                <div style={sectionLabelStyle}>
                  <SvgIcon name="alert-triangle" size={14} color={C.warning} /> Additional Details Required
                </div>
                <div style={{ fontSize: "12px", color: C.textSub, marginBottom: "12px" }}>
                  The hospital record has no value for these, so NHCX cannot be given a complete bundle.
                  Aadhaar and ABHA count as one requirement — either one is enough.
                </div>
                <div style={gridStyle}>
                  {revealed.map((name) => {
                    const meta = OVERRIDE_FIELDS[name] || { label: name };
                    const setOverride = (value) => setOverrides((p) => ({ ...p, [name]: value }));
                    return (
                      <Field key={name} label={meta.label} hint={meta.hint}>
                        {meta.type === "gender" ? (
                          <IpdTextField select fullWidth value={overrides[name] || ""} onChange={(e) => setOverride(e.target.value)}>
                            {GENDERS.map((g) => (
                              <MenuItem key={g} value={g}>{g}</MenuItem>
                            ))}
                          </IpdTextField>
                        ) : meta.type === "date" ? (
                          <IpdDateField fullWidth value={overrides[name] || ""} onChange={setOverride} maxDate={new Date()} />
                        ) : (
                          <IpdTextField fullWidth value={overrides[name] || ""} onChange={(e) => setOverride(e.target.value)} />
                        )}
                      </Field>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── ERRORS ── */}
            {error && <ErrorBlock error={error} />}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
              <IpdButton variant="outlined" onClick={handleSkip} disabled={submitting}>
                Skip — preview next screens
              </IpdButton>
              <IpdButton
                onClick={handleSubmit}
                disabled={submitting}
                loading={submitting}
                startIcon={<SvgIcon name="send" size={15} color="#fff" />}
              >
                {/* The revealed inputs stay on screen after a successful submit so the
                    values are visible rather than hidden-but-still-sent — so the label
                    keys off an outstanding error, not off `revealed` alone. */}
                {submitting ? "Submitting…" : error && revealed.length ? "Retry with details" : "Run Eligibility Check"}
              </IpdButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── phase: waiting ───────────────────────────────────────────────────────────

function WaitingState({ correlationId, apiCallId, attempts, exhausted, error, preview, onKeepWaiting, onSkipToResult, onStop }) {
  return (
    <div style={{ ...innerCardStyle, alignItems: "center", textAlign: "center", padding: "40px 20px", gap: "10px" }}>
      {preview && <PreviewBanner />}
      <SvgIcon name={exhausted ? "clock" : "sync"} size={26} color={C.primary} />
      <div style={{ fontSize: "15px", fontWeight: "700", color: C.text }}>
        {exhausted ? "Still no answer from the payer" : "Submitted to NHCX — waiting for the payer"}
      </div>
      <div style={{ fontSize: "13px", color: C.textSub, maxWidth: "460px" }}>
        {exhausted
          ? "The payer replies asynchronously and can take longer than a minute. Nothing is lost — keep waiting or come back and check again."
          : `The payer answers asynchronously. Checked ${attempts} time${attempts === 1 ? "" : "s"}…`}
      </div>
      <div style={{ fontSize: "11px", color: C.textSub, fontFamily: "monospace", marginTop: "4px" }}>
        correlation id · {correlationId}
      </div>
      {apiCallId && (
        <div style={{ fontSize: "11px", color: C.textSub, fontFamily: "monospace" }}>
          api call id · {apiCallId}
        </div>
      )}
      {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error.message}</div>}
      <div style={{ display: "flex", gap: "10px", marginTop: "8px", flexWrap: "wrap", justifyContent: "center" }}>
        <IpdButton variant="outlined" onClick={onSkipToResult}>
          Skip to result
        </IpdButton>
        {exhausted && (
          <>
            <IpdButton onClick={onKeepWaiting} startIcon={<SvgIcon name="refresh" size={14} color="#fff" />}>
              Keep waiting
            </IpdButton>
            <IpdButton variant="outlined" onClick={onStop}>Start over</IpdButton>
          </>
        )}
        {preview && !exhausted && (
          <IpdButton variant="outlined" onClick={onStop}>Start over</IpdButton>
        )}
      </div>
    </div>
  );
}

// ── phase: result ────────────────────────────────────────────────────────────

// The guide documents `benefit` and `errors` only as empty arrays, so both are read
// defensively against the FHIR CoverageEligibilityResponse shapes they come from, and
// fall back to whatever plain text is present rather than rendering "[object Object]".
const codeText = (c) =>
  (typeof c === "string" && c) || c?.text || c?.coding?.[0]?.display || c?.coding?.[0]?.code || "";

const moneyText = (m) => {
  if (m == null) return "";
  if (typeof m !== "object") return String(m);
  return [m.value, m.currency].filter((v) => v != null && v !== "").join(" ");
};

// FHIR splits the amount across allowed[x] / used[x] by datatype — take whichever is set.
function benefitAmount(b, prefix) {
  const money = moneyText(b[`${prefix}Money`]);
  if (money) return money;
  const plain = b[`${prefix}String`] ?? b[`${prefix}UnsignedInt`];
  return plain == null ? "" : String(plain);
}

// Returns "" when nothing readable is present, so callers can pick their own fallback.
const errorText = (e) => codeText(e?.code) || codeText(e) || e?.message || "";

function BenefitRows({ benefit }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "6px" }}>
      {benefit.map((b, i) => {
        const allowed = benefitAmount(b, "allowed");
        const used = benefitAmount(b, "used");
        return (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "12px" }}>
            <span style={{ color: C.textSub }}>{codeText(b.type) || "Benefit"}</span>
            <span style={{ fontWeight: "700", color: C.text, whiteSpace: "nowrap" }}>
              {allowed || "—"}
              {used ? ` · ${used} used` : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ResultState({ result, correlationId, apiCallId, onRunAgain, onCopyToManual, onContinueToPreauth, preview }) {
  const status = result?.status;
  const elig = result?.eligibility || {};
  const period = elig.benefit_period || {};
  const coverage = elig.insurance_items || [];

  if (status !== "complete") {
    // error_details has no documented shape, so read the readable fields first and
    // only fall back to dumping the object.
    const details = result?.error_details;
    const message =
      status === "decryption_error"
        ? "The server could not decrypt the payer's reply. This needs support — it is not something you can fix here."
        : (typeof details === "string" && details) ||
          details?.message ||
          errorText(details) ||
          "The payer returned an error for this request.";
    return (
      <div style={{ ...innerCardStyle, gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SvgIcon name="alert-triangle" size={16} color={C.danger} />
          <span style={{ fontSize: "14px", fontWeight: "700", color: C.text }}>Eligibility check failed</span>
          <Pill tone="red">{status || "unknown"}</Pill>
        </div>
        <div style={{ fontSize: "13px", color: C.textSub }}>{message}</div>
        <div style={{ fontSize: "10px", color: C.textSub, fontFamily: "monospace", wordBreak: "break-all" }}>
          <div>correlation · {correlationId}</div>
          {apiCallId && <div>api call · {apiCallId}</div>}
        </div>
        <div>
          <IpdButton variant="outlined" onClick={onRunAgain}>Run another check</IpdButton>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: "16px", alignItems: "start" }}>
      {preview && (
        <div style={{ gridColumn: "1 / -1" }}>
          <PreviewBanner />
        </div>
      )}
      {/* Verdict */}
      <div style={innerCardStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Verdict
          </span>
          <Pill tone={elig.inforce ? "green" : "red"}>{elig.inforce ? "In force" : "Not in force"}</Pill>
        </div>
        <div style={{ fontSize: "20px", fontWeight: "800", color: elig.inforce ? C.success : C.danger }}>
          {elig.inforce ? "Policy is active" : "Policy is not active"}
        </div>
        {elig.disposition && <div style={{ fontSize: "13px", color: C.textSub }}>{elig.disposition}</div>}
        {(period.start || period.end) && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
            <span style={{ color: C.textSub }}>Benefit period</span>
            <span style={{ fontWeight: "700", color: C.text }}>{period.start || "—"} → {period.end || "—"}</span>
          </div>
        )}
      </div>

      {/* Coverage */}
      <div style={innerCardStyle}>
        <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Coverage ({coverage.length})
        </span>
        {coverage.length === 0 ? (
          <div style={{ fontSize: "13px", color: C.textSub }}>The payer returned no itemised coverage.</div>
        ) : (
          coverage.map((c, i) => (
            <div key={i} style={{ borderTop: i ? `1px solid ${C.border}` : "none", paddingTop: i ? "10px" : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "13px", fontWeight: "700", color: C.text }}>{c.name || "—"}</span>
                {c.excluded ? <Pill tone="red">Excluded</Pill> : <Pill tone="green">Covered</Pill>}
                {c.authorization_required && <Pill tone="blue">Pre-auth needed</Pill>}
              </div>
              {c.description && <div style={{ fontSize: "12px", color: C.textSub, marginTop: "4px" }}>{c.description}</div>}
              {(c.benefit || []).length > 0 && <BenefitRows benefit={c.benefit} />}
            </div>
          ))
        )}
      </div>

      {/* Actions */}
      <div style={innerCardStyle}>
        <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Next
        </span>
        <div style={{ fontSize: "12px", color: C.textSub }}>
          NHCX results are held in the service's memory and are lost when it restarts — copy the
          outcome into the Manual tab to persist it against this admission.
        </div>
        {onContinueToPreauth && (
          <IpdButton onClick={onContinueToPreauth} startIcon={<SvgIcon name="arrow-right" size={14} color="#fff" />}>
            Continue to Pre-Auth
          </IpdButton>
        )}
        {onCopyToManual && (
          <IpdButton
            variant={onContinueToPreauth ? "outlined" : undefined}
            onClick={onCopyToManual}
            startIcon={<SvgIcon name="check" size={14} color={onContinueToPreauth ? C.primary : "#fff"} />}
          >
            Copy to Manual
          </IpdButton>
        )}
        <IpdButton variant="outlined" onClick={onRunAgain}>Run another check</IpdButton>
        {(elig.errors || []).length > 0 && (
          <div style={{ fontSize: "12px", color: C.danger, borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
            <div style={{ fontWeight: "700", marginBottom: "4px" }}>
              Reported by the payer ({elig.errors.length})
            </div>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              {elig.errors.map((e, i) => <li key={i}>{errorText(e) || "Unspecified error"}</li>)}
            </ul>
          </div>
        )}
        <div style={{ fontSize: "10px", color: C.textSub, fontFamily: "monospace", wordBreak: "break-all", borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
          <div>correlation · {correlationId}</div>
          {apiCallId && <div>api call · {apiCallId}</div>}
          {result?.received_at && <div>received · {result.received_at}</div>}
        </div>
      </div>
    </div>
  );
}

// ── errors ───────────────────────────────────────────────────────────────────

function ErrorBlock({ error }) {
  return (
    <div style={{ border: `1px solid #f5c2c0`, background: "#fdecec", borderRadius: "10px", padding: "14px 16px", display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <SvgIcon name="alert-triangle" size={15} color={C.danger} />
        <span style={{ fontSize: "13px", fontWeight: "700", color: C.danger }}>{error.message}</span>
      </div>
      {error.fieldErrors?.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "12px", color: C.text }}>
          {error.fieldErrors.map((f, i) => (
            <li key={i}><strong>{f.field}</strong> — {f.message}</li>
          ))}
        </ul>
      )}
      {/* A missing_fields entry that names no override can only be fixed in the EMR. */}
      {error.missing?.filter((m) => !m.overrides.length).map((m, i) => (
        <div key={i} style={{ fontSize: "12px", color: C.text }}>
          {m.message} <strong>Fix this on the patient record in the hospital system.</strong>
        </div>
      ))}
    </div>
  );
}
